import { Component, ChangeDetectionStrategy, ElementRef, ViewChild, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface AIFeature {
  title: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-recruiter-features',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './recruiter-features.component.html',
  styleUrls: ['./recruiter-features.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecruiterFeaturesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('featuresGrid') featuresGrid!: ElementRef<HTMLElement>;

  private scrollIntervals: any[] = [];
  private pauseTimeouts: any[] = [];

  constructor(private ngZone: NgZone) { }
  readonly features: AIFeature[] = [
    {
      title: 'AI Resume Screening',
      description: 'Automatically analyze resumes, evaluate candidate profiles, and shortlist the most qualified applicants based on job requirements',
      icon: 'scan'
    },
    {
      title: 'Smart Assessments',
      description: 'Generate skill-based assessments using AI in minutes, customize questions, and evaluate candidates with automated scoring.',
      icon: 'clipboard'
    },
    {
      title: 'AI Interviews',
      description: 'Conduct secure AI-powered video interviews with real-time proctoring, anti-cheating detection, and comprehensive candidate evaluatio',
      icon: 'video'
    },
    {
      title: 'AI Proctoring',
      description: 'Ensure assessment integrity with AI-powered proctoring, live monitoring, violation detection, and fraud prevention throughout the test.',
      icon: 'shield'
    },
    {
      title: 'Reports & Analytics',
      description: 'Manage candidates seamlessly across every hiring stage—from application and assessment to interview, shortlisting, and final selection.',
      icon: 'chart'
    },
    {
      title: 'Candidate Management',
      description: 'recordings, proctoring violation snapshots, and detailed performance analytics for confident hiring decisions.',
      icon: 'users'
    }
  ];

  ngAfterViewInit() {
    this.setupAutoScroll(this.featuresGrid?.nativeElement, 3800); // 3.8 seconds
  }

  ngOnDestroy() {
    this.scrollIntervals.forEach(interval => clearInterval(interval));
    this.pauseTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  private setupAutoScroll(element: HTMLElement | undefined, intervalMs: number = 3000) {
    if (!element) return;

    this.ngZone.runOutsideAngular(() => {
      let isPaused = false;
      let intervalId: any;

      const startScroll = () => {
        intervalId = setInterval(() => {
          if (isPaused || window.innerWidth >= 1024) return;

          let nextScroll = element.scrollLeft + element.clientWidth * 0.7;

          const maxScroll = element.scrollWidth - element.clientWidth;
          if (Math.ceil(element.scrollLeft) >= maxScroll - 10) {
            nextScroll = 0;
          } else if (nextScroll > maxScroll) {
            nextScroll = maxScroll;
          }

          element.scrollTo({ left: nextScroll, behavior: 'smooth' });
        }, intervalMs);
      };

      const pauseScroll = () => {
        isPaused = true;
        this.pauseTimeouts.push(
          setTimeout(() => {
            isPaused = false;
          }, 5000)
        );
      };

      element.addEventListener('touchstart', pauseScroll, { passive: true });
      element.addEventListener('pointerdown', pauseScroll, { passive: true });

      startScroll();
      this.scrollIntervals.push(intervalId);
    });
  }
}
